import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import { EDITOR_UPLOAD_PENDING_MESSAGE, editorUploadsPending } from "../../../components/RichTextEditor";
import endpoints from "../../../config/endpoints";
import { Sidebar } from "../../../layouts/Sidebar";
import { setLoading } from "../../../services/reducers/homeSlice";
import restApi from "../../../services/restApi";
import tools from "../../../utils/tools";
import ActivityLog from "../../../components/ActivityLog";
import Attributes from "./Attributes";
import Basic from "./Basic";
import Faq from "./Faq";
import FitFor from "./FitFor";
import Guarantee from "./Guarantee";
import Pictures from "./Pictures";
import Prices from "./Prices";
import Redirect from "./Redirect";
import Relations from "./Relations";
import Specifics from "./Specifics";

const ProductManage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [title, setTitle] = useState("");
  const [tab, setTab] = useState("basic");
  const [form, setForm] = useState<any>();
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [tags, setTags] = useState([]);
  const [parts, setParts] = useState([]);
  const [images, setImages] = useState([]);
  const [mainImageId, setMainImageId] = useState<any>(null);
  const [products, setProducts] = useState([]);
  const [specifics, setSpecifics] = useState([]);
  const [cars, setCars] = useState([]);
  const [years, setYears] = useState([]);
  const [colors, setColors] = useState([]);
  const [guaranteeCompanies, setGuaranteeCompanies] = useState([]);
  const { id: paramId } = useParams();
  const isEdit = window.location.pathname.includes("edit");
  const tabTitles = {
    basic: "اطلاعات اولیه",
    pictures: "تصاویر",
    prices: "قیمت ها",
    specifics: "مشخصات فنی",
    attributes: "شاخص ها",
    relations: "محصولات وابسته",
    // fitFor: "مناسب برای",
    redirect: "ریدایرکت",
    guarantee: "گارانتی",
    faqs: "سوالات متداول",
    ...(isEdit ? { activityLog: "تاریخچه تغییرات" } : {}),
  };

  const content = () => {
    switch (tab) {
      case "basic":
        return (
          <Basic
            form={form}
            setForm={setForm}
            categories={categories}
            tags={tags}
            brands={brands}
            cars={cars}
          />
        );
      case "pictures":
        return (
          <Pictures
            images={images}
            setImages={setImages}
            mainImageId={mainImageId}
            setMainImageId={setMainImageId}
          />
        );
      case "prices":
        return <Prices form={form} setForm={setForm} />;
      case "relations":
        return <Relations form={form} setForm={setForm} products={products} />;
      case "specifics":
        return (
          <Specifics
            form={form}
            setForm={setForm}
            specifics={specifics}
            parts={parts}
            fetchBrand={fetchPart}
          />
        );
      case "attributes":
        return (
          <Attributes
            form={form}
            setForm={setForm}
            specifics={specifics}
            images={images}
            colors={colors}
          />
        );
      case "fitFor":
        return (
          <FitFor form={form} setForm={setForm} cars={cars} years={years} />
        );
      case "redirect":
        return <Redirect form={form} setForm={setForm} />;
      case "guarantee":
        return (
          <Guarantee
            form={form}
            setForm={setForm}
            guaranteeCompanies={guaranteeCompanies}
          />
        );
      case "faqs":
        return <Faq form={form} setForm={setForm} />;
      case "activityLog":
        return <ActivityLog entityType="product" entityId={paramId} />;
      default:
        return <></>;
    }
  };

  const fetchPart = async (id) => {
    dispatch(setLoading(true));

    const res = await restApi(endpoints.part.single + id).get();

    if (res.code == 200) {
      setTitle((prev) => res.data.title);
      setSpecifics(res.data?.specifics);
    }

    dispatch(setLoading(false));
  };

  const submit = async () => {
    // an editor image still uploading would be saved without its address
    if (editorUploadsPending()) {
      Swal.fire({
        title: "صبر کنید",
        text: EDITOR_UPLOAD_PENDING_MESSAGE,
        icon: "warning",
        confirmButtonText: "متوجه شدم",
      });
      return;
    }
    dispatch(setLoading(true));

    const data = tools.extractor(form, [
      "price",
      "weight",
      "discountPercent",
      "discountPrice",
      "tags",
      "title",
      "brandId",
      "sort",
      "longText",
      "shortText",
      "status",
      "code",
      "carId",
      "slug",
      "isPre",
    ]);

    const productResult = await restApi(
      endpoints.product.basic + (isEdit ? paramId : ""),
    ).post(data);

    const productId = productResult.data?.id;

    const mediaData = new FormData();
    images.forEach((e) => {
      if (e.data) mediaData.append(`file${e.id}`, e.data);
    });

    if (productResult.code == 2001) {
      Swal.fire({
        title: "ناموفق",
        text: "URL تکراری میباشد",
        icon: "error",
        confirmButtonText: "متوجه شدم",
      });
      dispatch(setLoading(false));

      return;
    }

    const keepIds = images.filter((e) => !e.data).map((e) => e.id);
    const params = new URLSearchParams();
    if (keepIds.length) params.set("keepIds", keepIds.join(","));
    if (mainImageId != null) params.set("mainImageId", String(mainImageId));
    const mediaUrl = `${endpoints.product.medias}${productId}${params.toString() ? `?${params}` : ""}`;
    const mediaResult = await restApi(mediaUrl).upload(mediaData, {});
    const mediaMap: Record<string, number> = mediaResult.mediaMap || {};

    const resolvedAttributes = Object.fromEntries(
      Object.entries(form?.attributes || {}).map(([key, attr]: any) => {
        const resolved = { ...attr };
        if (
          typeof attr.imageId === "string" &&
          attr.imageId.startsWith("new+")
        ) {
          resolved.imageId = mediaMap[`file${attr.imageId}`] ?? undefined;
        }
        return [key, resolved];
      }),
    );

    const res = await Promise.all([
      restApi(endpoints.product.relations + productId).put(
        tools.extractor(form, ["required", "similar"]),
      ),
      restApi(endpoints.product.specifics + productId).put(
        tools.extractor(form, ["specifics", "partId"]),
      ),
      // restApi(endpoints.product.fitFors + productId).put(
      // tools.extractor(form, ["fitFors"]),
      // ),
      restApi(endpoints.product.attributes + productId).put({
        attributes: resolvedAttributes,
      }),
      // also sent when both fields were cleared, so the backend drops the
      // redirect; one field without the other comes back as an error
      (form?.oldUrl || form?.newUrl || form?.hadRedirect) &&
        restApi(endpoints.product.redirect + productId).put(
          tools.extractor(form, ["oldUrl", "newUrl"]),
        ),
      restApi(endpoints.product.guarantees + productId).put({
        guarantees: form?.guarantees || [],
      }),
      restApi(endpoints.product.faqs + productId).put({
        faqs: form?.faqs || [],
      }),
    ]);
    if (res.every((e) => (e?.code ? e.code == 200 : true))) {
      Swal.fire({
        title: "موفق",
        text: `محصول با موفقیت ${isEdit ? "ویرایش" : "ایجاد"} شد`,
        icon: "success",
        confirmButtonText: "متوجه شدم",
        didClose() {
          // navigate('/product')
        },
      });
    } else {
      Swal.fire({
        title: "ناموفق",
        text: "خطایی رخ داده",
        icon: "error",
        confirmButtonText: "متوجه شدم",
      });
    }
    dispatch(setLoading(false));
  };

  const fetchData = async () => {
    dispatch(setLoading(true));

    const res = await Promise.all([
      restApi(endpoints.category.index).get(),
      restApi(endpoints.brand.index).get(),
      restApi(endpoints.tag.index).get(),
      restApi(endpoints.part.index).get(),
      restApi(endpoints.product.index).get(),
      // restApi(endpoints.car.index).get(),
      restApi(endpoints.year).get(),
      restApi(endpoints.color.index).get(),
      paramId && restApi(endpoints.product.single + paramId).get(),
      restApi(endpoints.guaranteeCompany.index).get(),
    ]);

    setCategories(res[0].data);
    setBrands(res[1].data);
    setTags(res[2].data);
    setParts(res[3].data);
    setProducts(res[4].data);
    // setCars(res[5].data);
    setYears(res[5].data);
    setColors(res[6].data);
    setGuaranteeCompanies(res[8]?.data || []);

    if (paramId) {
      const imageDatas = res[7].data?.medias?.map((e) => ({
        preview: e.url,
        data: undefined,
        id: e.id,
      }));

      setImages(imageDatas);
      setMainImageId(
        imageDatas?.find((img) => img.preview === res[7].data?.mainImage)?.id ?? null,
      );

      setTitle(res[7].data?.title);

      await fetchPart(res[7].data?.partId);

      // format specifics
      const newSpecifics = {};

      res[7].data?.specificProducts?.map((e) => {
        newSpecifics[e.specificSlug] = {
          value: e.specificValueSlug,
          inTitle: e.inTitle || false,
          isAttribute: e.isAttribute || false,
          sort: e.sort,
        };
      });

      // format attributes
      const newAttributes = {};

      res[7].data?.attributes?.map((e, index) => {
        const newAttributeSpecifics = {};
        e.attributeSpecifics.map((v) => {
          newAttributeSpecifics[v.specificSlug] = v.specificValueSlug;
        });
        newAttributes[index] = {
          id: e.id,
          color: e.colorId,
          discountPrice: e.discountPrice,
          price: e.price,
          status: e.status,
          imageId: e.mediaId || undefined,
          specifics: newAttributeSpecifics,
        };
      });

      setForm({
        slug: res[7].data?.slug,
        sort: res[7].data?.sort,
        tags: res[7].data?.tags?.map((e) => e.id.toString()),
        categoryId: res[7].data?.categoryId,
        brandId: res[7].data?.brandId,
        status: res[7].data?.status,
        longText: res[7].data?.longText,
        shortText: res[7].data?.shortText,
        isPre: res[7].data?.isPre,
        price: res[7].data?.price,
        weight: res[7].data?.weight,
        code: res[7].data?.code,
        discountPrice: res[7].data?.discountPrice,
        discountPercent: res[7].data?.discountPercent ?? "",
        partId: res[7].data?.partId,
        carId: res[7].data?.carId,
        required: res[7].data?.requiredProducts?.map((e) => e.id.toString()),
        similar: res[7].data?.similarProducts?.map((e) => e.id.toString()),
        oldUrl: res[7].data?.redirect?.urlFrom,
        newUrl: res[7].data?.redirect?.urlTo,
        hadRedirect: !!res[7].data?.redirect,
        fitFors: res[7].data?.fitFors?.map((e) => ({
          car: e.carId,
          year: e.yearId,
          toYear: e.toYearId,
        })),
        specifics: newSpecifics,
        attributes: newAttributes,
        faqs: res[7].data?.faqs?.map((e) => ({
          question: e.question,
          answer: e.answer,
        })) || [],
        guarantees: res[7].data?.guarantees?.map((e) => ({
          id: e.id,
          title: e.title,
          days: e.days,
          guaranteeCompanyId: e.guaranteeCompanyId,
          status: e.status ?? true,
        })) || [],
      });
    }

    dispatch(setLoading(false));
  };

  useEffect(() => {
    fetchData();
  }, []);

  const formatTitle = () => {
    let newTitle = "";

    if (form?.partId) {
      newTitle = parts.find((e) => e.id == form?.partId)?.title;
    }

    if (form?.specifics) {
      const newSpecifics: any[] = Object.values(form?.specifics).filter(
        (e: any) => e.inTitle,
      ); //Apparently I created a variable for easier understanding

      const specificValues = [];
      specifics.map((i) =>
        i.specificValues?.map((j) => specificValues.push(j)),
      );
      newSpecifics
        .sort(
          (a, b) =>
            Number(a.sort || Number.MAX_SAFE_INTEGER) -
            Number(b.sort || Number.MAX_SAFE_INTEGER),
        )
        .map((e) => {
          const valueTitle = specificValues?.find(
            (i) => i.slug == (e?.value || ""),
          )?.title;
          if (valueTitle) {
            newTitle += " " + valueTitle;
          }
        });
    }

    if (form?.carId) {
      newTitle +=
        " " + cars.find((e) => e.id == form?.carId)?.slug.replaceAll("/", " ");
      // newTitle += ' ' + cars.find(e => e.id == form?.carId)?.title;
    }

    if (form?.brandId) {
      newTitle += " " + brands.find((e) => e.id == form?.brandId)?.title;
    }

    setForm((prev) => ({
      ...prev,
      title: newTitle,
    }));
  };

  useEffect(() => {
    formatTitle();
  }, [form?.brandId, form?.specifics, form?.partId]);

  return (
    <>
      <main className="dashboardBody">
        <Sidebar />
        <div className="dashBoardMain main">
          <div className="addInfoHeader">
            <div className="buttonContainer keepRight">
              <button
                className="dashboardHeader keepRight"
                onClick={() => submit()}
              >
                <p>{isEdit ? "ویرایش" : "ایجاد"} محصول</p>
              </button>
            </div>
            <span>
              <h1 className="sideBarTitle">بازگشت به صفحه محصولات</h1>
              <h1 className="dashBoardTitle">
                {isEdit ? "ویرایش محصول" : "افزودن محصول جدید"}
              </h1>
            </span>
            <i className="backAdd" onClick={() => navigate("/product")}></i>
          </div>
          <section className="center">
            <h1 className="rtl">{form?.title}</h1>
            <div className="dashTabs">
              {Object.entries(tabTitles).map(([key, value]) => (
                <span
                  className={`ordersTag ${key == tab ? "activeTab" : ""}`}
                  onClick={() => setTab(key)}
                >
                  {value}
                </span>
              ))}
            </div>
            {content()}
          </section>
        </div>
      </main>
    </>
  );
};

export default ProductManage;
